/**
 * YD Product Sequence Mobile
 * 移动端独立模块：跳过序列帧，用两个 LOOP 视频 + fade scrub 切换
 *
 * 状态流程:
 *   LOADING → READY → SCRUB_FADE
 *     │        │        │
 *     │        │        └─ ScrollTrigger scrub 驱动 LOOP1↔LOOP2 fade
 *     │        │
 *     │        └─ LOOP1 video canplay 后进入；
 *     │           做：隐藏 loading + 释放滚动锁 + dispatch ready
 *     │              + 播 LOOP1 + Title1 line reveal + 创建 ScrollTrigger
 *     │
 *     └─ 初始挂载状态，等待 LOOP1 canplay
 */
(function () {
  'use strict';

  // 仅移动端执行
  if (window.innerWidth >= 768) return;

  // 防重复初始化
  if (window.ydProductSequenceMobileInited) return;
  window.ydProductSequenceMobileInited = true;

  /**
   * 解析滚动高度配置（与桌面端 yd-product-sequence.js:388 同源）
   * 优先用 locked-vh 锁定值，避开 iOS Safari 地址栏导致的 svh 漂移
   */
  function parseScrollHeight(value) {
    const lockedVH = window.YDLockedVH && window.YDLockedVH.value;
    const viewportHeight = lockedVH ? lockedVH * 100 : window.innerHeight;
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && (value.endsWith('vh') || value.endsWith('svh'))) {
      return (parseFloat(value) / 100) * viewportHeight;
    }
    return viewportHeight; // 默认 100svh（桌面端 yd-product-sequence.js 同名函数默认 200svh，有意区分）
  }

  /**
   * 隐藏全屏 loading 屏（与桌面端一致）
   */
  function hideLoadingScreen() {
    const loadingScreen = document.getElementById('loading-screen');
    if (loadingScreen) loadingScreen.classList.add('fade-out');
  }

  /**
   * 初始化 SplitText - 把标题切成 .yd-line / .yd-line-inner
   * 防重入：如果已存在 .yd-line-inner，跳过（避免桌面端模块也切过一次）
   */
  function initSplitText(el) {
    if (!el) return;
    if (el.querySelector('.yd-line-inner')) return;

    const split = new SplitText(el, {
      type: 'lines',
      linesClass: 'yd-line'
    });

    split.lines.forEach((line) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'yd-line-inner';
      wrapper.innerHTML = line.innerHTML;
      line.innerHTML = '';
      line.appendChild(wrapper);
    });
  }

  /**
   * 进入 READY 状态：隐藏 loading、释放滚动锁、播 LOOP1、Title 1 入场
   */
  function enterReady(state) {
    if (state.ready) return;
    state.ready = true;

    hideLoadingScreen();
    // 移动端：release 交 YDMobileReady（need('intro') 已在 init 早期声明），这里只上报就绪；
    // 协调器未激活时照常立即 release
    if (window.YDMobileReady && window.YDMobileReady.active) {
      window.YDMobileReady.signal('intro');
    } else {
      window.__sequenceScrollRelease?.();
    }

    window.dispatchEvent(
      new CustomEvent('yd-product-sequence-ready', {
        detail: {
          state: 'READY',
          timestamp: Date.now(),
          device: 'mobile'
        }
      })
    );

    // 显示并播放 LOOP1
    gsap.set(state.loop1, { opacity: 1 });
    const playPromise = state.loop1.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        // iOS autoplay 被拦也忽略 — autoplay 属性会自己重试
      });
    }

    // Title 1 入场
    gsap.to(state.titleUniverseTime, {
      opacity: 1,
      duration: 0.4,
      ease: 'power2.out'
    });
    gsap.fromTo(
      state.titleUniverseTime.querySelectorAll('.yd-line-inner'),
      { yPercent: 100 },
      {
        yPercent: 0,
        duration: 0.8,
        stagger: 0.12,
        ease: 'power3.out',
        delay: 0.1
      }
    );

    // 只有 LOOP2 存在时才创建 fade scrub（否则 section 直接跟随页面滚走）
    if (state.loop2) {
      state.scrollTrigger = createScrollTriggerFade(state);
    } else {
      // 没 LOOP2：保留 LOOP1 满显，section 不 pin，跟着页面滚走
      console.log(
        '[YD ProductSequence Mobile] no loop2, skip ScrollTrigger fade'
      );
    }
  }

  /**
   * 创建 fade scrub ScrollTrigger
   * progress 0 → 1 映射到 fade 进度 t（在 fadeStart..fadeEnd 之间归一化）
   *   t=0  : LOOP1 + Title1 满显
   *   t=1  : LOOP2 + Title2 满显
   */
  function createScrollTriggerFade(state) {
    const { fadeStart, fadeEnd } = state.cfg;

    return ScrollTrigger.create({
      trigger: state.wrapper,
      start: 'top top',
      // 函数式 end：横竖屏切换后 refresh 时按新 --locked-vh 重算 pin 距离
      end: () => '+=' + parseScrollHeight(state.cfg.scrollHeight),
      pin: state.wrapper,
      pinSpacing: true,
      scrub: 1,
      refreshPriority: 10,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        const p = self.progress;
        const t = Math.min(
          1,
          Math.max(0, (p - fadeStart) / (fadeEnd - fadeStart))
        );

        gsap.set(state.loop1, { opacity: 1 - t });
        gsap.set(state.titleUniverseTime, { opacity: 1 - t });

        if (state.loop2) {
          gsap.set(state.loop2, { opacity: t });
          // 首次 t > 0 时启动 loop2 播放（一次性）
          if (t > 0 && !state.loop2Started) {
            state.loop2Started = true;
            // 与桌面端 finalLoopFps 对齐：FINAL_LOOP 阶段整体放慢
            state.loop2.playbackRate = state.cfg.loop2PlaybackRate;
            const promise = state.loop2.play();
            if (promise && typeof promise.catch === 'function') {
              promise.catch(() => {});
            }
          }
        }

        // Title 2 跟随 t 淡入，并在 t > 0.05 时一次性触发 line reveal
        gsap.set(state.titlePlayWithTime, { opacity: t });
        if (t > 0.05 && !state.titlePlayWithTimeRevealed) {
          state.titlePlayWithTimeRevealed = true;
          gsap.to(
            state.titlePlayWithTime.querySelectorAll('.yd-line-inner'),
            {
              yPercent: 0,
              duration: 0.8,
              stagger: 0.12,
              ease: 'power3.out'
            }
          );
        }
      }
    });
  }

  /**
   * 等待 GSAP / ScrollTrigger / SplitText 就绪
   */
  function waitForDependencies() {
    return new Promise((resolve, reject) => {
      const ready = () =>
        typeof gsap !== 'undefined' &&
        typeof ScrollTrigger !== 'undefined' &&
        typeof SplitText !== 'undefined';

      if (ready()) {
        resolve();
        return;
      }

      if (window.GSAPUtils && typeof window.GSAPUtils.waitFor === 'function') {
        const timer = setTimeout(
          () => reject(new Error('GSAP/ScrollTrigger/SplitText not loaded')),
          10000
        );
        window.GSAPUtils.waitFor(['ScrollTrigger', 'SplitText'], () => {
          clearTimeout(timer);
          resolve();
        });
        return;
      }

      const pollTimeout = setTimeout(() => {
        clearInterval(check);
        reject(new Error('GSAP/ScrollTrigger/SplitText not loaded'));
      }, 10000);
      const check = setInterval(() => {
        if (ready()) {
          clearInterval(check);
          clearTimeout(pollTimeout);
          resolve();
        }
      }, 200);
    });
  }

  /**
   * 入口
   */
  async function init() {
    // 提前声明 intro 信号：让协调器从一开始就把 intro 纳入"待齐信号"，
    // 避免其他信号先到齐时在 intro 就绪前放行（intro 就绪才解锁滚动的意图）。
    if (window.YDMobileReady && window.YDMobileReady.active) {
      window.YDMobileReady.need('intro');
    }

    try {
      await waitForDependencies();
    } catch (err) {
      console.warn('[YD ProductSequence Mobile] deps not loaded:', err);
      hideLoadingScreen();
      // 异常降级：消化已声明的 intro need，避免协调器悬空等待
      if (window.YDMobileReady && window.YDMobileReady.active) window.YDMobileReady.signal('intro');
      window.__sequenceScrollRelease?.();
      return;
    }

    const wrapper = document.querySelector('#sequence-canvas-wrapper');
    const mobileEl = document.querySelector('#product-sequence-mobile');
    const loop1 = document.querySelector('#ps-mobile-loop1');
    const loop2 = document.querySelector('#ps-mobile-loop2');
    const titleUniverseTime = document.querySelector('#title-universe-time');
    const titlePlayWithTime = document.querySelector('#title-play-with-time');

    // 关键元素缺失：释放滚动锁让页面可用，退出
    if (!wrapper || !mobileEl || !loop1 || !titleUniverseTime || !titlePlayWithTime) {
      console.warn(
        '[YD ProductSequence Mobile] missing required elements, abort'
      );
      hideLoadingScreen();
      // 异常降级：消化已声明的 intro need，避免协调器悬空等待
      if (window.YDMobileReady && window.YDMobileReady.active) window.YDMobileReady.signal('intro');
      window.__sequenceScrollRelease?.();
      return;
    }

    let cfg;
    try {
      cfg = JSON.parse(mobileEl.dataset.mobileConfig || '{}');
    } catch (e) {
      cfg = {};
    }
    cfg.fadeStart = cfg.fadeStart ?? 0.3;
    cfg.fadeEnd = cfg.fadeEnd ?? 0.7;
    cfg.scrollHeight = cfg.scrollHeight || '100svh';
    // FINAL_LOOP 视频播放速率，0.5 = 慢一倍
    cfg.loop2PlaybackRate = cfg.loop2PlaybackRate > 0 ? cfg.loop2PlaybackRate : 1;

    const state = {
      wrapper,
      mobileEl,
      loop1,
      loop2, // 可能为 null（用户只配了 LOOP1）
      titleUniverseTime,
      titlePlayWithTime, // 与 titleUniverseTime 同为必填（liquid 内无条件渲染），由 init 守卫确保非空
      cfg,
      ready: false,
      loop2Started: false,
      titlePlayWithTimeRevealed: false,
      scrollTrigger: null
    };

    // SplitText 初始化（移动端独立做一次，桌面端模块在此设备不会执行）
    initSplitText(titleUniverseTime);
    initSplitText(titlePlayWithTime);

    // 准备 Title 2 在 fade 前的初始态：透明 + line yPercent 100
    gsap.set(titlePlayWithTime, { opacity: 0 });
    gsap.set(titlePlayWithTime.querySelectorAll('.yd-line-inner'), {
      yPercent: 100
    });
    gsap.set(titleUniverseTime, { opacity: 0 });
    gsap.set(titleUniverseTime.querySelectorAll('.yd-line-inner'), {
      yPercent: 100
    });

    console.log('[YD ProductSequence Mobile] SplitText ready');

    // 5s 超时兜底
    const fallbackTimer = setTimeout(() => {
      if (!state.ready) {
        console.warn(
          '[YD ProductSequence Mobile] canplay timeout, force ready'
        );
        enterReady(state);
      }
    }, 5000);

    const onCanPlay = () => {
      clearTimeout(fallbackTimer);
      enterReady(state);
    };

    if (loop1.readyState >= 3) {
      // HAVE_FUTURE_DATA — 已可播
      onCanPlay();
    } else {
      loop1.addEventListener('canplay', onCanPlay, { once: true });
    }

    loop1.addEventListener(
      'error',
      () => {
        clearTimeout(fallbackTimer);
        console.warn('[YD ProductSequence Mobile] loop1 video error');
        enterReady(state); // 至少释放锁让页面可用
      },
      { once: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
